import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import BudgetSectionLine from './section/BudgetSectionLine';
import BudgetElementLine from './element/BudgetElementLine';
import BudgetPageBreakLine from './pageBreak/BudgetPageBreakLine';
import BudgetNoteLine from './note/BudgetNoteLine';
import styles from './BudgetLine.module.scss';

import BudgetLineMenu from '../lineMenu/BudgetLineMenu';
import { forwardRef, memo, Ref, useContext, useState } from 'react';
import { addNewParameters, convertParameterExpressions, getFamilyParametersForBudgetLine, getLastInLevelKey, getLevelFromParentKeys, getTotalSectionLines, isLineBeingCut, lineInnerActiveStyle, lineInnerLevelStyle, lineLevelStyle, lineOutterLineStyle, mergeWithExistingParameters } from '../utils';
import { useDispatch, useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { addLine, calculateAllLinesTotals, calculateAllLinesTotalsOnly, calculateBudgetTotals, calculateLineTotals, cloneLine, copyLines, cutLines, hydrateClipboardFromStorage, moveLinesDown, moveLinesUp, pasteLines, removeLines, selectRangeTo, setLines, setSelectedLineKey, setSelectedLineKeys, toggleSelectedLineKey, updateLine, updateLinesExpressionsValues, updateLinesNumbers } from 'store/budget/action';
import { AtLeast } from 'common/types/Atleast';
import { AddLinePlacement } from 'store/budget/type';
import Utils from 'common/services/Utils';
import store from 'store/store';
import { getTotalLinesInside, isTopLevelElementLine } from 'store/budget/reducer';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';
import { TreeItemComponentProps } from 'common/components/sortableTree/types';
import clsx from 'clsx';
import useIsMobileOrTablet from 'common/hooks/useDeviceType';
import Toast from 'common/services/Toast';
import { useTranslation } from 'react-i18next';
import BudgetsService from 'api/budgets/BudgetsService';
import FamiliesService from 'api/families/FamiliesService';
import { ParameterSectionParameterType } from 'common/components/parametersSections/models';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'common/components/modal/Modal';
import { Header, Text } from 'common/components/texts/Texts';
import Button from 'common/components/button/Button';
import SaveComponentModal from '../saveComponentModal/SaveComponentModal';
import ImportComponentModal from '../importComponentModal/ImportComponentModal';
import { LinesDataProvider } from '../LinesDataProvider';

export const BUDGET_LINE_ID_PREFIX = 'budgetLine_';

function BudgetLine({
    item: line,
    depth: level,
    handleProps,
    clone,
    ghost,
    disableInteraction,
    disableSelection,
    style,
    parentsKeys,
    childCount,
    disabled,
    wrapperRef,
}: TreeItemComponentProps<BudgetLineDto>, ref: Ref<HTMLDivElement>): JSX.Element | null {
    const { t } = useTranslation();
    const { isComponentEditor } = useContext(LinesDataProvider);
    const isLockedRootSection = (isComponentEditor ?? false)
        && line.lineType === BudgetLineType.SECTION
        && level === 0;
    const isLockedRootSectionChild = (isComponentEditor ?? false) && level === 1;
    const selectedLineKeys = useSelector<Reducers, string[]>(x => x.budget.selectedLineKeys);
    const primarySelectedKey = selectedLineKeys.length > 0 ? selectedLineKeys[selectedLineKeys.length - 1] : null;
    const selectionCount = selectedLineKeys.length;
    // Both selectors use cached helpers keyed on the lines array reference, so the
    // O(n) work runs once per dispatch instead of once per BudgetLine instance.
    const totalRootSections = useSelector<Reducers, number>(x => getTotalSectionLines(x.budget.lines));
    // Nearest (deepest) selected ancestor of this line, used so descendants render
    // inside its border exactly like single-select does today.
    const selectedAncestorKey = parentsKeys.length > 0
        ? [...parentsKeys].reverse().find(k => selectedLineKeys.includes(k)) ?? null
        : null;
    const parentIsSelected = selectedAncestorKey != null;
    const isLastInSelectedLevel = useSelector<Reducers, boolean>(
        x => selectedAncestorKey != null
            ? getLastInLevelKey(x.budget.lines, selectedAncestorKey) === line.key
            : false,
    );
    const dispatch = useDispatch();
    const isSelected = selectedLineKeys.includes(line.key);
    const isPrimary = primarySelectedKey === line.key;
    const isActive = isSelected;
    const [isEditing, setIsEditing] = useState((line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT) && !line.elementId && !line.isCustomElement && !line.designation);
    const isMobileOrTablet = useIsMobileOrTablet();
    const isBeingCut = useSelector<Reducers, boolean>(x => x.budget.copyType === 'cut' && x.budget.copiedLineKeys.length > 0 && isLineBeingCut(x.budget.lines, line.key, x.budget.copiedLineKeys));
    const copiedLineKeys = useSelector<Reducers, string[]>(x => x.budget.copiedLineKeys);
    const [isLoading, setIsLoading] = useState(false);
    const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [showSaveComponentModal, setShowSaveComponentModal] = useState(false);
    const [showImportComponentModal, setShowImportComponentModal] = useState(false);
    const [importPlacement, setImportPlacement] = useState<AddLinePlacement>(AddLinePlacement.INSIDE);

    const onClickLine = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        if (isLockedRootSection) {
            return;
        }
        const { selectedLineKeys: currentSelected, selectionAnchorKey } = store.getState().budget;

        if (e.shiftKey && selectionAnchorKey) {
            dispatch(selectRangeTo(line.key));
        } else if (e.ctrlKey || e.metaKey) {
            dispatch(toggleSelectedLineKey(line.key));
        } else if (currentSelected.length !== 1 || currentSelected[0] !== line.key) {
            dispatch(setSelectedLineKeys([line.key], line.key));
        }
    }

    const onAddedElement = () => {
        setIsEditing(false);
    }

    const onClone = () => {
        const newLineKey = Utils.newGuid();
        dispatch(cloneLine(line.key, newLineKey));

        setTimeout(() => {
            dispatch(setSelectedLineKey(newLineKey));
        }, 0);
    }

    const getActiveSelectionKeys = (): string[] => {
        const current = store.getState().budget.selectedLineKeys;
        return current.length > 0 ? current : [line.key];
    };

    const onCopy = () => {
        const activeKeys = getActiveSelectionKeys();
        const alreadyCopied = copiedLineKeys.length === activeKeys.length
            && activeKeys.every(k => copiedLineKeys.includes(k));
        dispatch(copyLines(alreadyCopied ? [] : activeKeys));
    }

    const onCut = () => {
        const activeKeys = getActiveSelectionKeys();
        const alreadyCut = copiedLineKeys.length === activeKeys.length
            && activeKeys.every(k => copiedLineKeys.includes(k));
        dispatch(cutLines(alreadyCut ? [] : activeKeys));
    }

    const onPaste = () => {
        // Refresh the clipboard from storage first so a copy made in another tab
        // (after this page was opened) is picked up. Synchronous, so state below is current.
        dispatch(hydrateClipboardFromStorage());
        const sourceKeys = store.getState().budget.copiedLineKeys;
        if (!sourceKeys.length) {
            Toast.warning(t('budgets.budget.lines.floating_menu.nothing_to_paste'));
            return;
        }
        const newKeysByOldKey: Record<string, string> = {};
        const newRootKeys: string[] = [];
        for (const oldKey of sourceKeys) {
            const newKey = Utils.newGuid();
            newKeysByOldKey[oldKey] = newKey;
            newRootKeys.push(newKey);
        }
        dispatch(pasteLines(line.key, newKeysByOldKey));

        dispatch(updateLine({
            key: line.key,
            isOpen: true,
        }));

        // Defer selection so it runs after the body click handler clears it
        setTimeout(() => {
            dispatch(setSelectedLineKeys(newRootKeys, newRootKeys[newRootKeys.length - 1] ?? null));
        }, 0);
    }

    const onAdd = (lineType: BudgetLineType, placement: AddLinePlacement) => {
        const newLineKey = Utils.newGuid();
        dispatch(addLine(lineType, placement, line.key, newLineKey));

        if (line.lineType === BudgetLineType.SECTION) {
            dispatch(updateLine({
                key: line.key,
                isOpen: true,
            }));
        }

        dispatch(setSelectedLineKey(newLineKey));
    }

    const onChange = (newLine: AtLeast<BudgetLineDto, 'key'>, calculateTotals: boolean) => {
        dispatch(updateLine(newLine));

        if (calculateTotals) {
            dispatch(calculateLineTotals(line.key));
        }

        if ('numberOverride' in newLine) {
            dispatch(updateLinesNumbers());
        }
    }

    const onRemove = () => {
        setShowRemoveConfirm(true);
    }

    const onRemoveConfirm = () => {
        const keysToRemove = getActiveSelectionKeys();
        const linesState = store.getState().budget.lines;
        const parentKeysToRecalc: string[] = [];
        const seenParents = new Set<string>();
        for (const key of keysToRemove) {
            const removedLine = linesState.find((l: BudgetLineDto) => l.key === key);
            const parentKey = removedLine?.parentKey;
            if (parentKey && !seenParents.has(parentKey)) {
                seenParents.add(parentKey);
                parentKeysToRecalc.push(parentKey);
            }
        }
        dispatch(removeLines(keysToRemove));
        dispatch(setSelectedLineKey(null));
        if (parentKeysToRecalc.length > 0) {
            for (const parentKey of parentKeysToRecalc) {
                dispatch(calculateLineTotals(parentKey));
            }
        } else {
            dispatch(calculateBudgetTotals());
        }
        setShowRemoveConfirm(false);
    }

    const onRefreshClick = () => {
        setShowRefreshConfirm(true);
    };

    const onRefreshConfirm = (mode: 'cancel' | 'new_only' | 'update_all') => {
        setShowRefreshConfirm(false);
        if (mode !== 'cancel') {
            onRefresh(mode);
        }
    };

    const onRefresh = async (mode: 'new_only' | 'update_all') => {
        if (disabled) {
            setIsLoading(() => false);
            return;
        }

        const mergeFn = mode === 'update_all' ? mergeWithExistingParameters : addNewParameters;

        try {
            setIsLoading(() => true);

            if (line.elementId) {
                const parametersData = await BudgetsService.getElementParameters(line.elementId, false);
                const newParams = parametersData.parameters ?? [];
                const convertedParams = newParams.map(p => convertParameterExpressions(p, newParams));
                const merged = mergeFn(convertedParams, line.parameters);
                onChange({
                    key: line.key,
                    parameters: merged,
                    grossUnitPrice: mode === 'update_all' && parametersData.price ? parametersData.price : line.grossUnitPrice,
                    changedParameters: true
                }, true);
            } else if ((line.isCustomElement || line.lineType === BudgetLineType.COMPONENT) && line.parameters?.length > 0) {
                const emptyGuid = '00000000-0000-0000-0000-000000000000';
                const findFamilyId = (params: BudgetLineParameterDto[]): string | undefined => {
                    for (const p of params) {
                        if (p.familyId && p.familyId !== emptyGuid) return p.familyId;
                        if (p.children?.length) {
                            const childResult = findFamilyId(p.children);
                            if (childResult) return childResult;
                        }
                    }
                    return undefined;
                };
                const findFamilyParameterId = (params: BudgetLineParameterDto[]): string | undefined => {
                    for (const p of params) {
                        if (p.familyParameterId) return p.familyParameterId;
                        if (p.children?.length) {
                            const childResult = findFamilyParameterId(p.children);
                            if (childResult) return childResult;
                        }
                    }
                    return undefined;
                };

                let familyId = findFamilyId(line.parameters);
                if (!familyId) {
                    const familyParameterId = findFamilyParameterId(line.parameters);
                    if (familyParameterId) {
                        familyId = await FamiliesService.getFamilyIdByParameter(familyParameterId) ?? undefined;
                    }
                }

                if (familyId) {
                    const newParameters = await getFamilyParametersForBudgetLine(familyId);
                    const preservedTypes = [ParameterSectionParameterType.PRODUCTS, ParameterSectionParameterType.IMAGES, ParameterSectionParameterType.NUMBER_MIN_MAX];
                    const existingPreserved = line.parameters.filter(p => p.parameterType && preservedTypes.includes(p.parameterType));
                    const existingMergeable = line.parameters.filter(p => !p.parameterType || !preservedTypes.includes(p.parameterType));
                    const merged = mergeFn(newParameters, existingMergeable);
                    onChange({
                        key: line.key,
                        parameters: [...merged, ...existingPreserved].sort((a, b) => a.position - b.position),
                        changedParameters: true
                    }, true);
                }
            }
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get the element data', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            setIsLoading(() => false);
        }
    }

    const onToggleDataSheet = () => {
        onChange({ key: line.key, enableDataSheet: !line.enableDataSheet }, false);
    }

    const onMoveUp = () => {
        const activeKeys = getActiveSelectionKeys();
        dispatch(moveLinesUp(activeKeys));
        if (line.parentKey) {
            dispatch(calculateLineTotals(line.parentKey));
        }
    }

    const onMoveDown = () => {
        const activeKeys = getActiveSelectionKeys();
        dispatch(moveLinesDown(activeKeys));
        if (line.parentKey) {
            dispatch(calculateLineTotals(line.parentKey));
        }
    }

    let content: JSX.Element | null;

    switch (line.lineType) {
        case BudgetLineType.SECTION:
            content = <BudgetSectionLine
                ref={ref}
                line={line}
                disabled={disabled || isLockedRootSection}
                level={level}
                isActive={isActive}
                onChange={onChange}
                childCount={childCount ?? 0}
            />
            break;
        case BudgetLineType.ELEMENT:
            content = <BudgetElementLine
                ref={ref}
                line={line}
                disabled={disabled}
                level={level}
                isActive={isActive}
                isEditing={isEditing}
                onChange={onChange}
                onAddedElement={onAddedElement}
                isComponentElement={false}
                isComponent={false}
            />
            break;
        case BudgetLineType.COMPONENT_ELEMENT:
            content = <BudgetElementLine
                ref={ref}
                line={line}
                disabled={disabled}
                level={level}
                isActive={isActive}
                isEditing={isEditing}
                onChange={onChange}
                onAddedElement={onAddedElement}
                isComponentElement={true}
                isComponent={false}
            />
            break;
        case BudgetLineType.COMPONENT:
            content = <BudgetElementLine
                ref={ref}
                line={line}
                disabled={disabled}
                level={level}
                isActive={isActive}
                isEditing={isEditing}
                onChange={onChange}
                onAddedElement={onAddedElement}
                isComponentElement={false}
                isComponent={true}
            />
            break;
        case BudgetLineType.PAGE_BREAK:
            content = <BudgetPageBreakLine
                ref={ref}
                line={line}
                disabled={disabled}
                level={level}
                isActive={isActive}
                onChange={onChange}
            />
            break;
        case BudgetLineType.NOTE:
            content = <BudgetNoteLine
                ref={ref}
                line={line}
                disabled={disabled}
                level={level}
                isActive={isActive}
                onChange={onChange}
            />
            break;
        default:
            console.warn(`Couldn't find a line type with the name '${line.lineType}'`);
            return null;
    }

    if (clone) {
        return null;
    }

    const levelsUntilParent = selectedAncestorKey != null
        ? getLevelFromParentKeys(selectedAncestorKey, parentsKeys)
        : 0;

    // The floating menu (which includes copy even in read-only/details mode) only
    // renders above the primary selected line, so flatten the top-left corner exactly
    // when it does — otherwise the rounded corner shows through broken under the tab.
    const borderTopLeftWithoutRadius = isPrimary || !isActive;

    return (
        <div
            className={clsx(
                styles.line,
                clone && styles.lineClone,
                ghost && styles.lineGhost,
                ghost && styles.indicator,
                disableSelection && styles.lineDisableSelection,
                disableInteraction && styles.lineDisableIteraction,
                isBeingCut && styles.isBeingCut,
            )}
            onClick={onClickLine}
            style={{
                ...style,
                ...lineLevelStyle(level, clone ?? false, levelsUntilParent),
                ...lineOutterLineStyle(isActive),
            }}
            id={BUDGET_LINE_ID_PREFIX + line.key}
            ref={wrapperRef as any}
        >
            {isPrimary && (
                <BudgetLineMenu
                    disabled={disabled}
                    showRemove={!disabled && !isLockedRootSection && !isLockedRootSectionChild && !(totalRootSections === 1 && line.lineType === BudgetLineType.SECTION && level === 0)}
                    showAdd={!disabled && !isLockedRootSection && selectionCount <= 1}
                    showClone={!disabled && !isLockedRootSection && !isLockedRootSectionChild && selectionCount <= 1}
                    showCopy={!isLockedRootSection && !isLockedRootSectionChild}
                    showCut={!disabled && !isLockedRootSection && !isLockedRootSectionChild}
                    showPaste={!disabled && !isBeingCut && !isLockedRootSectionChild && selectionCount <= 1 && (line.lineType === BudgetLineType.SECTION || line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT)}
                    showPreview={selectionCount <= 1 && (line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT) && Boolean(line.elementId)}
                    showToggleDataSheet={selectionCount <= 1 && (line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT) && Boolean(line.elementId)}
                    showEdit={selectionCount <= 1 && !disabled && (line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT)}
                    showEditCancel={selectionCount <= 1 && !disabled && (line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT) && isEditing && (Boolean(line.elementId) || Boolean(line.isCustomElement))}
                    showMove={!isMobileOrTablet && !disabled && !isLockedRootSection && !isLockedRootSectionChild}
                    showMoveUp={isMobileOrTablet && !disabled && !isLockedRootSection && !isLockedRootSectionChild}
                    showMoveDown={isMobileOrTablet && !disabled && !isLockedRootSection && !isLockedRootSectionChild}
                    showRefresh={selectionCount <= 1 && !disabled && (line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT) && (Boolean(line.elementId) || Boolean(line.isCustomElement) || (line.lineType === BudgetLineType.COMPONENT && line.parameters?.length > 0))}
                    isEditing={isEditing}
                    isEnableDataSheet={Boolean(line.enableDataSheet)}
                    isLoading={isLoading}
                    level={level}
                    handleProps={handleProps}
                    line={line}
                    selectionCount={selectionCount}
                    onRemove={onRemove}
                    onAdd={onAdd}
                    onClone={onClone}
                    onCopy={onCopy}
                    onCut={onCut}
                    onPaste={onPaste}
                    onEdit={(v) => setIsEditing(v)}
                    onPreview={() => {
                        if (line.elementId) {
                            const elementTypeUrl = line.lineType === BudgetLineType.COMPONENT_ELEMENT ? 'components-items' : 'elements-items';
                            window.open('/library/' + elementTypeUrl + '/details/' + line.elementId, '_blank');
                        }
                    }}
                    onToggleDataSheet={onToggleDataSheet}
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    onRefresh={onRefreshClick}
                    showSaveAsComponent={selectionCount <= 1 && !disabled && !isLockedRootSection && !isComponentEditor && (line.lineType === BudgetLineType.SECTION || line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT)}
                    showImportComponent={!disabled && !isLockedRootSection && selectionCount <= 1}
                    onSaveAsComponent={() => setShowSaveComponentModal(true)}
                    onImportComponent={(placement) => {
                        setImportPlacement(placement);
                        setShowImportComponentModal(true);
                    }}
                />
            )}
            <div
                className={styles.contentContainer}
                style={{
                    ...(lineInnerLevelStyle(parentIsSelected, levelsUntilParent)),
                    ...(lineInnerActiveStyle(isActive, parentIsSelected, isLastInSelectedLevel, (childCount ?? 0) > 0, line.isOpen ?? false, borderTopLeftWithoutRadius)),
                }}
            >
                {content}
            </div>
            {showSaveComponentModal && (
                <SaveComponentModal
                    isOpen={showSaveComponentModal}
                    line={line}
                    onClose={(savedComponentId) => {
                        setShowSaveComponentModal(false);
                        if (savedComponentId) {
                            dispatch(updateLine({
                                key: line.key,
                                sourceBudgetComponentId: savedComponentId,
                            }));
                        }
                    }}
                />
            )}
            {showImportComponentModal && (
                <ImportComponentModal
                    isOpen={showImportComponentModal}
                    onClose={(importedLines, componentId) => {
                        setShowImportComponentModal(false);
                        if (importedLines && importedLines.length > 0) {
                            // Insert the imported lines into the budget
                            const currentLines = [...store.getState().budget.lines];
                            const targetKey = line.key;

                            // Set parent keys based on placement
                            const rootLines = importedLines.filter(l => !l.parentKey || !importedLines.find(il => il.key === l.parentKey));
                            rootLines.forEach(rl => {
                                if (importPlacement === AddLinePlacement.INSIDE) {
                                    rl.parentKey = targetKey;
                                } else if (importPlacement === AddLinePlacement.SAME) {
                                    rl.parentKey = line.parentKey;
                                } else {
                                    rl.parentKey = null;
                                }
                            });

                            // Find insertion point (after parent + all its children, so it goes at the end)
                            const targetIndex = currentLines.findIndex(l => l.key === targetKey);
                            const totalChildren = targetIndex >= 0 ? getTotalLinesInside(currentLines, targetKey) : 0;
                            const insertIndex = targetIndex >= 0 ? targetIndex + 1 + totalChildren : currentLines.length;

                            // Apply budget defaults to imported top-level element lines
                            const budgetData = store.getState().budget.budget;
                            const allLinesForCheck = [
                                ...currentLines.slice(0, insertIndex),
                                ...importedLines,
                                ...currentLines.slice(insertIndex),
                            ];
                            importedLines.forEach(il => {
                                if (isTopLevelElementLine(il, allLinesForCheck)) {
                                    il.decennialInsurancePercentage = budgetData.defaultDecennialInsurancePercentage ?? 0;
                                    il.decennialInsurancePercentageExpression = ExpressionSystem.serializeParameterMentionId(
                                        ExpressionSystem.formatParameterId(['budget', 'defaultDecennialInsurancePercentage']));
                                }
                            });

                            // Insert lines
                            const newLines = [
                                ...currentLines.slice(0, insertIndex),
                                ...importedLines,
                                ...currentLines.slice(insertIndex),
                            ];

                            dispatch(setLines(newLines));
                            dispatch(updateLinesExpressionsValues());
                            dispatch(calculateAllLinesTotalsOnly());
                            dispatch(calculateBudgetTotals());

                            if (importPlacement === AddLinePlacement.INSIDE) {
                                dispatch(updateLine({
                                    key: targetKey,
                                    isOpen: true,
                                }));
                            }
                        }
                    }}
                />
            )}
            {showRefreshConfirm && (
                <Modal isOpen={showRefreshConfirm} style={{ content: { minWidth: 400 } }} onRequestClose={() => onRefreshConfirm('cancel')}>
                    <div>
                        <ModalHeader>
                            <Header>{t('budgets.budget.lines.floating_menu.refresh').toUpperCase()}</Header>
                        </ModalHeader>
                        <ModalBody>
                            <Text style={{ whiteSpace: 'pre-line' }}>{t('budgets.budget.lines.floating_menu.refresh_confirm')}</Text>
                        </ModalBody>
                        <ModalFooter className="text-right">
                            <Button fw variant='secondary' onClick={() => onRefreshConfirm('cancel')}>{t('common.cancel')}</Button>
                            <Button fw onClick={() => onRefreshConfirm('new_only')}>{t('budgets.budget.lines.floating_menu.refresh_new_only')}</Button>
                            <Button fw onClick={() => onRefreshConfirm('update_all')}>{t('budgets.budget.lines.floating_menu.refresh_update_all')}</Button>
                        </ModalFooter>
                    </div>
                </Modal>
            )}
            {showRemoveConfirm && (() => {
                const removeCount = Math.max(getActiveSelectionKeys().length, 1);
                return (
                    <Modal isOpen={showRemoveConfirm} style={{ content: { minWidth: 400 } }} onRequestClose={() => setShowRemoveConfirm(false)}>
                        <div onClick={(e) => e.stopPropagation()}>
                            <ModalHeader>
                                <Header>{t('budgets.budget.lines.floating_menu.remove', { count: removeCount }).toUpperCase()}</Header>
                            </ModalHeader>
                            <ModalBody>
                                <Text style={{ whiteSpace: 'pre-line' }}>
                                    {t('budgets.budget.lines.floating_menu.remove_confirm', { count: removeCount })}
                                </Text>
                            </ModalBody>
                            <ModalFooter className="text-right">
                                <Button fw variant='secondary' onClick={() => setShowRemoveConfirm(false)}>{t('common.cancel')}</Button>
                                <Button fw onClick={onRemoveConfirm}>{t('common.delete')}</Button>
                            </ModalFooter>
                        </div>
                    </Modal>
                );
            })()}
        </div>
    );
}

export default memo(
    forwardRef<HTMLDivElement, TreeItemComponentProps<BudgetLineDto>>(BudgetLine)
);
